import type { EnvVar } from '@/api/schemas';
import { formatDateTime, timeAgo } from '@/lib/format';
import { stagger } from '@/lib/motion';
import { ScopeChip, Tag, VisibilityBadge } from '@/components/badges';
import { Card } from '@/components/ui/card';
import { RowMenu } from './EnvVarRowMenu';
import { WarningChips } from './EnvVarWarningChips';
import type { EnvVarActions } from './EnvVarsTable';

interface ListProps {
  vars: EnvVar[];
  now: number;
  actions: EnvVarActions;
}

/** Phone layout: one card per variable. */
export function EnvVarsCards({ vars, now, actions }: ListProps) {
  return (
    <ul className="flex flex-col gap-3 p-4">
      {vars.map((v, i) => (
        <li key={v.id} className="enter" style={stagger(Math.min(i, 10))}>
          <EnvVarCard v={v} now={now} actions={actions} />
        </li>
      ))}
    </ul>
  );
}

function EnvVarCard({ v, now, actions }: { v: EnvVar; now: number; actions: EnvVarActions }) {
  return (
    <Card data-testid={`env-row-${v.key}`} className="gap-2 bg-secondary/40 p-4 shadow-none">
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <div className="break-all font-mono text-[13px] font-medium text-foreground">{v.key}</div>
          {v.description ? <div className="text-xs text-muted-foreground">{v.description}</div> : null}
        </div>
        <RowMenu v={v} {...actions} />
      </div>
      <CardBadges v={v} />
      <div className="flex items-center justify-between font-mono text-[11px] text-subtle">
        <span>{v.currentVersion?.valueFingerprint ?? '—'}</span>
        <span title={formatDateTime(v.updatedAt)}>{timeAgo(v.updatedAt, now)}</span>
      </div>
      <WarningChips v={v} />
    </Card>
  );
}

function CardBadges({ v }: { v: EnvVar }) {
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <VisibilityBadge visibility={v.visibility} />
      <Tag>{v.valueType}</Tag>
      {v.configTime ? <Tag title="Also used when the app config is read">app config</Tag> : null}
      {v.inherited ? <ScopeChip scope={v.scope} environment={v.environment} platform={v.platform} className="text-subtle" /> : null}
      {v.currentVersion ? <Tag>v{v.currentVersion.version}</Tag> : null}
    </div>
  );
}
